import { useEffect } from "react";
import { useFieldArray, useFormContext, useWatch } from "react-hook-form";

import Chip from "@mui/material/Chip";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

import {
  Button,
  CustomAccordion,
  FormPercentageField,
  FormTextField,
} from "@covertree/ui";

import { communityConfigurationCardSx } from "./communityConfigurationStyles";

import type { CommunityConfigurationTaxDefault } from "@graphql/api";
import type { CommunityConfigurationFormValues } from "./communitiesConfigurationForm";

interface StatePoliciesSectionProps {
  taxDefaults: CommunityConfigurationTaxDefault[];
}

interface LocalTaxesEditorProps {
  localTaxIndex: number;
  ownerClientId: string;
  ownerName?: string;
  stateIndex: number;
}

function LocalTaxesEditor({
  localTaxIndex,
  ownerClientId,
  ownerName,
  stateIndex,
}: LocalTaxesEditorProps) {
  const { control, getValues, setValue } =
    useFormContext<CommunityConfigurationFormValues>();
  const otherTaxesPath =
    `states.${stateIndex}.localTaxes.${localTaxIndex}.otherLocalTaxes` as const;
  const { append, fields, remove } = useFieldArray({
    control,
    keyName: "formKey",
    name: otherTaxesPath,
  });

  const removeLocalTaxes = () => {
    const localTaxes = getValues(`states.${stateIndex}.localTaxes`);
    setValue(
      `states.${stateIndex}.localTaxes`,
      localTaxes.filter((tax) => tax.ownerClientId !== ownerClientId),
      { shouldDirty: true }
    );
  };

  return (
    <Stack spacing={2}>
      <Typography fontWeight={600}>Local taxes for {ownerName}</Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} md={3}>
          <FormTextField
            control={control}
            fullWidth
            id={`state-${stateIndex}-local-tax-${localTaxIndex}-city`}
            label="City"
            name={`states.${stateIndex}.localTaxes.${localTaxIndex}.cityName`}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <FormPercentageField
            control={control}
            fullWidth
            id={`state-${stateIndex}-local-tax-${localTaxIndex}-city-rate`}
            label="City tax rate"
            name={`states.${stateIndex}.localTaxes.${localTaxIndex}.cityTaxRate`}
            nullOnEmpty
            optional
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <FormTextField
            control={control}
            fullWidth
            id={`state-${stateIndex}-local-tax-${localTaxIndex}-county`}
            label="County"
            name={`states.${stateIndex}.localTaxes.${localTaxIndex}.countyName`}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <FormPercentageField
            control={control}
            fullWidth
            id={`state-${stateIndex}-local-tax-${localTaxIndex}-county-rate`}
            label="County tax rate"
            name={`states.${stateIndex}.localTaxes.${localTaxIndex}.countyTaxRate`}
            nullOnEmpty
            optional
          />
        </Grid>
      </Grid>

      {fields.map((otherTax, taxIndex) => (
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={1}
          key={otherTax.formKey}>
          <FormTextField
            control={control}
            fullWidth
            id={`state-${stateIndex}-local-tax-${localTaxIndex}-other-${taxIndex}-name`}
            label="Local tax name"
            name={`states.${stateIndex}.localTaxes.${localTaxIndex}.otherLocalTaxes.${taxIndex}.name`}
            required
          />
          <FormPercentageField
            control={control}
            id={`state-${stateIndex}-local-tax-${localTaxIndex}-other-${taxIndex}-rate`}
            label="Rate"
            name={`states.${stateIndex}.localTaxes.${localTaxIndex}.otherLocalTaxes.${taxIndex}.rate`}
            required
          />
          <Button color="error" type="button" onClick={() => remove(taxIndex)}>
            Remove
          </Button>
        </Stack>
      ))}

      <Stack direction="row" spacing={1}>
        <Button
          size="small"
          type="button"
          onClick={() => append({ name: "", rate: 0 })}>
          Add another local tax
        </Button>
        <Button
          size="small"
          color="error"
          type="button"
          onClick={removeLocalTaxes}>
          Remove local taxes
        </Button>
      </Stack>
    </Stack>
  );
}

interface OwnerStatePolicyCardProps {
  ownerClientId: string;
  stateIndex: number;
  taxDefaults: CommunityConfigurationTaxDefault[];
}

function OwnerStatePolicyCard({
  ownerClientId,
  stateIndex,
  taxDefaults,
}: OwnerStatePolicyCardProps) {
  const { control, getValues, setValue } =
    useFormContext<CommunityConfigurationFormValues>();
  const state = useWatch({ control, name: `states.${stateIndex}` });
  const communities = useWatch({ control, name: "communities" }) ?? [];
  const owners = useWatch({ control, name: "owners" }) ?? [];
  const owner = owners.find(
    (candidate) => candidate.clientId === ownerClientId
  );
  const policyNumberIndex = state.policyNumbers.findIndex(
    (entry) => entry.ownerClientId === ownerClientId
  );
  const localTaxIndex = state.localTaxes.findIndex(
    (entry) => entry.ownerClientId === ownerClientId
  );
  const isRequired = communities.some(
    (community) =>
      community.state === state.state &&
      community.ownerClientId === ownerClientId &&
      community.isActive
  );

  const addLocalTaxes = () => {
    const currentState = getValues(`states.${stateIndex}`);
    if (
      currentState.localTaxes.some((tax) => tax.ownerClientId === ownerClientId)
    ) {
      return;
    }
    const otherFee = taxDefaults.find(
      (taxDefault) => taxDefault.state === currentState.state
    )?.otherFee;
    setValue(
      `states.${stateIndex}.localTaxes`,
      [
        ...currentState.localTaxes,
        {
          cityName: "",
          cityTaxRate: null,
          countyName: "",
          countyTaxRate: null,
          otherLocalTaxes: otherFee
            ? [{ name: otherFee.name, rate: otherFee.rate }]
            : [],
          ownerClientId,
        },
      ],
      { shouldDirty: true }
    );
  };

  if (policyNumberIndex < 0) return null;

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack spacing={2}>
        <FormTextField
          control={control}
          fullWidth
          id={`state-${stateIndex}-owner-${ownerClientId}-policy-number`}
          label={`${owner?.name || "Owner"} policy number for ${state.state}`}
          name={`states.${stateIndex}.policyNumbers.${policyNumberIndex}.policyNumber`}
          required={isRequired}
        />

        {localTaxIndex < 0 ? (
          <Button size="small" type="button" onClick={addLocalTaxes}>
            Add local taxes
          </Button>
        ) : (
          <LocalTaxesEditor
            localTaxIndex={localTaxIndex}
            ownerClientId={ownerClientId}
            ownerName={owner?.name}
            stateIndex={stateIndex}
          />
        )}
      </Stack>
    </Paper>
  );
}

export function StatePoliciesSection({
  taxDefaults,
}: StatePoliciesSectionProps) {
  const { control, getValues, setValue } =
    useFormContext<CommunityConfigurationFormValues>();
  const states = useWatch({ control, name: "states" }) ?? [];
  const communities = useWatch({ control, name: "communities" }) ?? [];

  useEffect(() => {
    const currentStates = getValues("states");
    let changed = false;
    const nextStates = currentStates.map((state) => {
      const representedOwnerIds = Array.from(
        new Set(
          communities
            .filter(
              (community) =>
                community.state === state.state && community.ownerClientId
            )
            .map((community) => community.ownerClientId!)
        )
      );
      const missingOwnerIds = representedOwnerIds.filter(
        (ownerClientId) =>
          !state.policyNumbers.some(
            (policyNumber) => policyNumber.ownerClientId === ownerClientId
          )
      );
      if (!missingOwnerIds.length) return state;
      changed = true;
      return {
        ...state,
        policyNumbers: [
          ...state.policyNumbers,
          ...missingOwnerIds.map((ownerClientId) => ({
            ownerClientId,
            policyNumber: "",
          })),
        ],
      };
    });
    if (changed) {
      setValue("states", nextStates, { shouldDirty: true });
    }
  }, [communities, getValues, setValue]);

  return (
    <Stack sx={communityConfigurationCardSx} spacing={2}>
      <Typography variant="h4">State policies, taxes, and fees</Typography>
      {states.map((state, stateIndex) => {
        const representedOwnerIds = Array.from(
          new Set(
            communities
              .filter(
                (community) =>
                  community.state === state.state && community.ownerClientId
              )
              .map((community) => community.ownerClientId!)
          )
        );
        return (
          <CustomAccordion
            key={state.state}
            expandOnInit
            withoutAccent
            withoutBorder
            header={() => (
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="h5">{state.state}</Typography>
                <Chip
                  size="small"
                  label={`${representedOwnerIds.length} owner combinations`}
                />
              </Stack>
            )}
            content={
              <>
                <Grid container spacing={2}>
                  {(
                    [
                      ["Surplus lines tax rate", "surplusLinesTax"],
                      ["Stamping fee rate", "stampingFee"],
                      ["State tax rate", "stateTax"],
                    ] as const
                  ).map(([label, fieldName]) => (
                    <Grid item xs={12} md={4} key={fieldName}>
                      <FormPercentageField
                        control={control}
                        fullWidth
                        id={`state-${stateIndex}-${fieldName}`}
                        label={label}
                        name={`states.${stateIndex}.${fieldName}`}
                        optional
                      />
                    </Grid>
                  ))}
                </Grid>

                <Typography variant="h6" mt={3} mb={1}>
                  Policy numbers by legal entity owner
                </Typography>
                <Stack spacing={2}>
                  {representedOwnerIds.map((ownerClientId) => (
                    <OwnerStatePolicyCard
                      key={ownerClientId}
                      ownerClientId={ownerClientId}
                      stateIndex={stateIndex}
                      taxDefaults={taxDefaults}
                    />
                  ))}
                </Stack>
              </>
            }
          />
        );
      })}
    </Stack>
  );
}
